import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import UserStatsCard from './components/UserStatsCard';
import UserTable from './components/UserTable';
import ActivityFeed from './components/ActivityFeed';
import UserFilters from './components/UserFilters';
import { userProfilesService, auditLogsService } from '../../services/userManagementService';

const UserManagementDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
    
    // Subscribe to real-time updates
    const unsubscribeUsers = userProfilesService?.subscribe(() => {
      fetchData();
    });

    const unsubscribeAudit = auditLogsService?.subscribe(() => {
      fetchActivities();
    });

    return () => {
      unsubscribeUsers?.();
      unsubscribeAudit?.();
    };
  }, []);

  useEffect(() => {
    applyFilters();
  }, [users, filters, searchTerm]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersResult, statsResult] = await Promise.all([
        userProfilesService?.getAll(),
        userProfilesService?.getStatistics()
      ]);

      if (usersResult?.data) {
        setUsers(usersResult?.data);
      }

      if (statsResult?.data) {
        setStats(statsResult?.data);
      }

      await fetchActivities();
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const { data } = await auditLogsService?.getRecentActivity(20);
      if (data) {
        setActivities(data);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...users];

    // Apply role filter
    if (filters?.role) {
      filtered = filtered?.filter(user => user?.role === filters?.role);
    }

    // Apply status filter
    if (filters?.status) {
      filtered = filtered?.filter(user => user?.status === filters?.status);
    }

    // Apply search
    if (searchTerm) {
      const search = searchTerm?.toLowerCase();
      filtered = filtered?.filter(user => 
        user?.fullName?.toLowerCase()?.includes(search) ||
        user?.email?.toLowerCase()?.includes(search)
      );
    }

    setFilteredUsers(filtered);
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const { error } = await userProfilesService?.updateRole(userId, newRole);
      if (!error) {
        // Log the action
        await auditLogsService?.create({
          userId: userId,
          action: 'role_change',
          details: `Role changed to ${newRole}`,
          ipAddress: 'N/A'
        });
        fetchData();
      }
    } catch (error) {
      console.error('Error updating role:', error);
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      const { error } = await userProfilesService?.updateStatus(userId, newStatus);
      if (!error) {
        fetchData();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleResetFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const statsCards = [
    {
      title: 'Total Users',
      value: stats?.totalUsers?.toString() || '0',
      icon: 'Users',
      iconColor: 'text-primary',
      iconBg: 'bg-primary/10',
      description: 'All registered users'
    },
    {
      title: 'Active Users',
      value: stats?.activeUsers?.toString() || '0',
      icon: 'UserCheck',
      iconColor: 'text-success',
      iconBg: 'bg-success/10',
      description: 'Currently active'
    },
    {
      title: 'Admins',
      value: stats?.adminCount?.toString() || '0',
      icon: 'Shield',
      iconColor: 'text-error',
      iconBg: 'bg-error/10',
      description: 'Administrator accounts'
    },
    {
      title: 'Suspended',
      value: stats?.suspendedUsers?.toString() || '0',
      icon: 'AlertTriangle',
      iconColor: 'text-warning',
      iconBg: 'bg-warning/10',
      description: 'Suspended accounts'
    }
  ];

  return (
    <>
      <Helmet>
        <title>User Management Dashboard | PaymentGateway</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">User Management</h1>
            <p className="text-muted-foreground">Manage dashboard users, assign roles, and monitor activities</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {statsCards?.map((card, index) => (
              <UserStatsCard key={index} {...card} />
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* User Table Section */}
            <div className="lg:col-span-2 space-y-6">
              <UserFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onSearch={handleSearch}
                onReset={handleResetFilters}
              />
              <UserTable
                users={filteredUsers}
                onRoleChange={handleRoleChange}
                onStatusChange={handleStatusChange}
                loading={loading}
              />
            </div>

            {/* Activity Feed Section */}
            <div className="lg:col-span-1">
              <ActivityFeed activities={activities} loading={loading} />
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default UserManagementDashboard;